import { useState, useMemo } from 'react';
import { X, DollarSign, Check, CreditCard, Gift, Users2, ArrowRight, Wallet, Copy } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { CURRENT_USD } from '../../utils/constants';

// Gift card brands available for purchase
export const GIFT_CARDS = [
  { id: 'gc-amazon', brand: 'Amazon', logo: 'A', color: 'bg-orange-500', reward: '2% Cashback' },
  { id: 'gc-starbucks', brand: 'Starbucks', logo: 'S', color: 'bg-green-600', reward: '3% Cashback' },
  { id: 'gc-target', brand: 'Target', logo: 'T', color: 'bg-red-600', reward: '2.5% Cashback' },
  { id: 'gc-apple', brand: 'Apple', logo: '', color: 'bg-gray-800', reward: '1.5% Cashback' },
  { id: 'gc-uber', brand: 'Uber', logo: 'U', color: 'bg-black', reward: '2% Cashback' },
  { id: 'gc-nike', brand: 'Nike', logo: 'N', color: 'bg-black', reward: '4% Cashback' },
  { id: 'gc-walmart', brand: 'Walmart', logo: 'W', color: 'bg-blue-600', reward: '2% Cashback' },
  { id: 'gc-bestbuy', brand: 'Best Buy', logo: 'BB', color: 'bg-yellow-500', reward: '3% Cashback' },
  { id: 'gc-netflix', brand: 'Netflix', logo: 'N', color: 'bg-red-700', reward: '2.5% Cashback' },
  { id: 'gc-spotify', brand: 'Spotify', logo: 'S', color: 'bg-green-500', reward: '3% Cashback' },
  { id: 'gc-doordash', brand: 'DoorDash', logo: 'DD', color: 'bg-red-500', reward: '4% Cashback' },
  { id: 'gc-sephora', brand: 'Sephora', logo: 'S', color: 'bg-black', reward: '5% Cashback' },
  { id: 'gc-homedepo', brand: 'Home Depot', logo: 'HD', color: 'bg-orange-600', reward: '2% Cashback' },
  { id: 'gc-costco', brand: 'Costco', logo: 'C', color: 'bg-red-600', reward: '1.5% Cashback' },
  { id: 'gc-airbnb', brand: 'Airbnb', logo: 'A', color: 'bg-rose-500', reward: '3.5% Cashback' },
  { id: 'gc-grubhub', brand: 'Grubhub', logo: 'GH', color: 'bg-orange-500', reward: '4% Cashback' },
];

const PRESET_AMOUNTS = ['10', '25', '50', '100', '250'];

// Generate a random redemption code
function generateRedemptionCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const segments = [];
  for (let i = 0; i < 4; i++) {
    let segment = '';
    for (let j = 0; j < 4; j++) {
      segment += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    segments.push(segment);
  }
  return segments.join('-');
}

export default function GiftCardFlow({ selectedCard, onClose }) {
  const { savedCards, joinedGroups } = useAppState();

  const [step, setStep] = useState('amount'); // 'amount' | 'recipient' | 'payment' | 'success'
  const [amount, setAmount] = useState('');
  const [recipientType, setRecipientType] = useState(null); // 'myself' | 'someone' | 'group'
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(savedCards?.[0] || null);
  const [copied, setCopied] = useState(false);

  // Generate redemption code once per purchase (memoized)
  const redemptionCode = useMemo(() => generateRedemptionCode(), []);

  if (!selectedCard) return null;

  // Parse cashback percentage from reward string (e.g., "2% Cashback" -> 2)
  const cashbackPercent = parseFloat(selectedCard.reward) || 2;
  const cashbackAmount = amount ? (parseFloat(amount) * cashbackPercent / 100).toFixed(2) : '0.00';

  const canProceedRecipient = recipientType === 'myself' ||
    (recipientType === 'someone' && recipientEmail) ||
    (recipientType === 'group' && selectedGroup);

  const handleClose = () => {
    setStep('amount');
    setAmount('');
    setRecipientType(null);
    setRecipientEmail('');
    setSelectedGroup(null);
    onClose();
  };

  const handleConfirmPurchase = () => {
    setStep('success');
  };

  // Step: Amount Selection
  if (step === 'amount') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Buy Gift Card</h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Card Preview */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5 flex items-center gap-3">
                <div className={`w-12 h-12 ${selectedCard.color} rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                  {selectedCard.logo}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{selectedCard.brand}</h3>
                  <p className="text-emerald-600 font-bold text-xs uppercase tracking-tight">{selectedCard.reward}</p>
                </div>
              </div>

              {/* Preset Amounts */}
              <div className="space-y-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Amount</p>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setAmount(preset)}
                      className={`py-3 rounded-xl font-bold text-sm transition-all ${
                        amount === preset
                          ? 'bg-indigo-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      ${preset}
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="mt-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Or Enter Custom</p>
                  <div className="relative">
                    <DollarSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full bg-gray-100 px-5 py-3 pl-10 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              {amount && parseFloat(amount) >= 5 && (
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-gray-700">Gift Card Value</span>
                    <span className="text-sm font-bold text-gray-900">${amount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-700">Cashback ({cashbackPercent}%)</span>
                    <span className="text-sm font-bold text-emerald-600">+${cashbackAmount}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setStep('recipient')}
                disabled={!amount || parseFloat(amount) < 5}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                Choose Recipient <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Recipient Selection
  if (step === 'recipient') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Choose Recipient</h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Card Summary */}
              <div className="bg-gray-50 rounded-xl p-3 mb-5 flex items-center gap-3">
                <div className={`w-10 h-10 ${selectedCard.color} rounded-lg flex items-center justify-center text-white font-bold shadow-sm`}>
                  {selectedCard.logo}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-sm text-gray-900">{selectedCard.brand} Gift Card</h3>
                  <p className="text-indigo-600 font-bold text-xs">${amount}</p>
                </div>
              </div>

              {/* Recipient Options */}
              <div className="space-y-3">
                {/* For Myself */}
                <button
                  onClick={() => setRecipientType('myself')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                    recipientType === 'myself' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white hover:border-indigo-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${recipientType === 'myself' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <Gift size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm">For Myself</h4>
                    <p className="text-xs text-gray-500">Add to my wallet</p>
                  </div>
                  {recipientType === 'myself' && <Check size={18} className="text-indigo-600" />}
                </button>

                {/* Send to Someone */}
                <button
                  onClick={() => setRecipientType('someone')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    recipientType === 'someone' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white hover:border-indigo-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${recipientType === 'someone' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <Gift size={20} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-sm">Send to Someone</h4>
                      <p className="text-xs text-gray-500">Gift via email</p>
                    </div>
                    {recipientType === 'someone' && <Check size={18} className="text-indigo-600" />}
                  </div>
                  {recipientType === 'someone' && (
                    <div className="mt-3">
                      <input
                        type="email"
                        placeholder="Enter recipient's email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-white px-4 py-3 rounded-lg text-sm font-bold outline-none border border-gray-200 focus:border-indigo-300"
                      />
                    </div>
                  )}
                </button>

                {/* Gift to Group */}
                {joinedGroups?.length > 0 && (
                  <button
                    onClick={() => setRecipientType('group')}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      recipientType === 'group' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white hover:border-indigo-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${recipientType === 'group' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <Users2 size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 text-sm">Gift to Group</h4>
                        <p className="text-xs text-gray-500">Shared with members</p>
                      </div>
                      {recipientType === 'group' && <Check size={18} className="text-indigo-600" />}
                    </div>
                    {recipientType === 'group' && (
                      <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                        {joinedGroups.map((group) => (
                          <button
                            key={group.id}
                            onClick={() => setSelectedGroup(group)}
                            className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                              selectedGroup?.id === group.id ? 'border-indigo-500 bg-indigo-100' : 'border-gray-200 bg-white'
                            }`}
                          >
                            <span className="font-bold text-sm text-gray-900">{group.name}</span>
                            {selectedGroup?.id === group.id && <Check size={14} className="ml-auto text-indigo-600" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setStep('payment')}
                disabled={!canProceedRecipient}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                Select Payment <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Payment Method
  if (step === 'payment') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Payment Method</h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Summary Card */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${selectedCard.color} rounded-lg flex items-center justify-center text-white font-bold shadow-sm`}>
                    {selectedCard.logo}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm text-gray-900">{selectedCard.brand} Gift Card</h3>
                    <p className="text-xs text-gray-500">
                      {recipientType === 'myself' && 'For myself'}
                      {recipientType === 'someone' && `To: ${recipientEmail}`}
                      {recipientType === 'group' && `To: ${selectedGroup?.name}`}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-indigo-600">${amount}</span>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Payment Method</p>

                {/* Bonndle Rewards Option */}
                {(() => {
                  const rewardsBalance = parseFloat(CURRENT_USD.replace(/[$,]/g, ''));
                  const purchaseAmount = parseFloat(amount) || 0;
                  const hasSufficientBalance = rewardsBalance >= purchaseAmount;

                  return (
                    <button
                      onClick={() => hasSufficientBalance && setSelectedPayment({ id: 'rewards', type: 'rewards' })}
                      disabled={!hasSufficientBalance}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                        selectedPayment?.id === 'rewards'
                          ? 'border-indigo-600 bg-indigo-50'
                          : hasSufficientBalance
                            ? 'border-gray-100 bg-white hover:border-indigo-200'
                            : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <div className="w-10 h-7 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Wallet size={14} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 text-sm">Bonndle Rewards</p>
                        <p className={`text-xs ${hasSufficientBalance ? 'text-emerald-600' : 'text-red-500'}`}>
                          Balance: {CURRENT_USD}
                          {!hasSufficientBalance && ' (Insufficient)'}
                        </p>
                      </div>
                      {selectedPayment?.id === 'rewards' && <Check size={18} className="text-indigo-600" />}
                    </button>
                  );
                })()}

                {/* Saved Cards */}
                {savedCards?.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setSelectedPayment(card)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selectedPayment?.id === card.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white'
                    }`}
                  >
                    <div className="w-10 h-7 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                      <CreditCard size={14} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 capitalize text-sm">{card.type} •••• {card.last4}</p>
                      <p className="text-xs text-gray-500">Expires {card.expiry}</p>
                    </div>
                    {selectedPayment?.id === card.id && <Check size={18} className="text-indigo-600" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleConfirmPurchase}
                disabled={!selectedPayment}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50"
              >
                Pay ${amount}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Success
  if (step === 'success') {
    const handleCopyCode = () => {
      navigator.clipboard.writeText(redemptionCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Purchase Complete!</h2>
              <p className="text-gray-500 text-sm mb-4">
                Your {selectedCard.brand} gift card has been
                {recipientType === 'myself' && ' added to your wallet'}
                {recipientType === 'someone' && ` sent to ${recipientEmail}`}
                {recipientType === 'group' && ` shared with ${selectedGroup?.name}`}
              </p>
              <div className="bg-emerald-50 px-4 py-2 rounded-full inline-block mb-5">
                <span className="text-emerald-700 font-bold text-sm">+${cashbackAmount} cashback earned!</span>
              </div>
              <div className={`w-20 h-20 ${selectedCard.color} rounded-xl flex items-center justify-center text-white font-bold text-3xl shadow-lg mx-auto mb-3`}>
                {selectedCard.logo}
              </div>
              <p className="text-2xl font-bold text-gray-900">${amount}</p>
              <p className="text-gray-500 text-sm mb-5">{selectedCard.brand} Gift Card</p>

              {/* Redemption Code - Only shown for self-purchases */}
              {recipientType === 'myself' && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Redemption Code</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-lg font-mono font-bold text-gray-900 tracking-wider">{redemptionCode}</code>
                    <button
                      onClick={handleCopyCode}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                    >
                      {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
                    </button>
                  </div>
                  {copied && <p className="text-xs text-emerald-600 font-medium mt-1">Copied to clipboard!</p>}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleClose}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
}
